import { Button, Input, Label } from "@repo/ui";
import { Plus, RefreshCw, Trash2 } from "lucide-react";

export interface CandidatRow {
  emailCandidat: string;
  nomCandidat: string;
  prenomCandidat: string;
  token: string;
}

interface CandidatsTableSectionProps {
  candidats: CandidatRow[];
  anonyme: boolean;
  onUpdate: (index: number, field: keyof CandidatRow, value: string) => void;
  onRemove: (index: number) => void;
  onAdd: () => void;
  onGenererToken: (index: number) => void;
}

/**
 * Id d'un champ de la n-ième ligne candidat.
 *
 * Chaque ligne répète les mêmes libellés (« Prénom », « Nom »…) : sans id
 * distinct, un seul `htmlFor` pointerait plusieurs champs et le lien
 * label ↔ champ ne voudrait plus rien dire. La ligne, elle, porte son propre
 * nom accessible (« Candidat 1 »), ce qui évite d'allonger chaque libellé
 * visible en « Prénom du candidat 1 ».
 */
const idChamp = (index: number, champ: keyof CandidatRow) =>
  `candidat-${index}-${champ}`;

export function CandidatsTableSection({
  candidats,
  anonyme,
  onUpdate,
  onRemove,
  onAdd,
  onGenererToken,
}: CandidatsTableSectionProps) {
  const validCount = candidats.filter((c) =>
    anonyme
      ? c.token
      : c.emailCandidat && c.nomCandidat && c.prenomCandidat,
  ).length;

  return (
    <div className="space-y-3">
      <div className="flex items-center justify-between">
        {/* Un `<span>` et non un `<Label>` : cet intitulé désigne une SECTION,
            pas un champ. Un `<label>` sans cible est annoncé comme un libellé
            orphelin par un lecteur d'écran, et le clic dessus ne fait rien. */}
        <span className="flex items-center gap-2 text-sm leading-none font-medium select-none">
          Candidats ({validCount})
          <span className="text-muted-foreground font-normal">(optionnel)</span>
        </span>
      </div>

      {candidats.map((c, index) => (
        // Groupe nommé : un lecteur d'écran annonce « Candidat 1 » en entrant
        // dans la ligne, donc « Prénom » suffit ensuite. Sans ça, une campagne
        // à cinq candidats présente quinze champs aux libellés identiques.
        <div
          key={index}
          role="group"
          aria-label={`Candidat ${index + 1}`}
          className="flex items-end gap-2"
        >
          {!anonyme && (
            <div className="grid gap-1 flex-1">
              <Label className="text-xs" htmlFor={idChamp(index, "prenomCandidat")}>
                Prénom
              </Label>
              <Input
                id={idChamp(index, "prenomCandidat")}
                value={c.prenomCandidat}
                onChange={(e) => onUpdate(index, "prenomCandidat", e.target.value)}
                placeholder="John"
              />
            </div>
          )}
          {!anonyme && (
            <div className="grid gap-1 flex-1">
              <Label className="text-xs" htmlFor={idChamp(index, "nomCandidat")}>
                Nom
              </Label>
              <Input
                id={idChamp(index, "nomCandidat")}
                value={c.nomCandidat}
                onChange={(e) => onUpdate(index, "nomCandidat", e.target.value)}
                placeholder="Doe"
              />
            </div>
          )}
          {!anonyme && (
            <div className="grid gap-1 flex-1">
              <Label className="text-xs" htmlFor={idChamp(index, "emailCandidat")}>
                Email
              </Label>
              <Input
                id={idChamp(index, "emailCandidat")}
                type="email"
                value={c.emailCandidat}
                onChange={(e) => onUpdate(index, "emailCandidat", e.target.value)}
                placeholder="john@example.com"
              />
            </div>
          )}
          {anonyme && (
            <div className="grid gap-1 flex-1">
              <Label className="text-xs" htmlFor={idChamp(index, "token")}>
                Token
              </Label>
              <div className="flex gap-1">
                <Input
                  id={idChamp(index, "token")}
                  value={c.token}
                  onChange={(e) => onUpdate(index, "token", e.target.value)}
                  placeholder="abc-1234"
                />
                <Button
                  type="button"
                  variant="outline"
                  size="icon"
                  aria-label={`Générer un nouveau code pour le candidat ${index + 1}`}
                  onClick={() => onGenererToken(index)}
                >
                  <RefreshCw className="h-3 w-3" />
                </Button>
              </div>
            </div>
          )}
          <Button
            type="button"
            variant="ghost"
            size="icon"
            aria-label={`Retirer le candidat ${index + 1}`}
            onClick={() => onRemove(index)}
          >
            <Trash2 className="h-4 w-4 text-destructive" />
          </Button>
        </div>
      ))}

      <Button
        type="button"
        variant="outline"
        size="sm"
        onClick={onAdd}
        className="w-full"
      >
        <Plus className="h-4 w-4 mr-1" />
        Ajouter un candidat
      </Button>
    </div>
  );
}
